/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import ChatInitializerContext from "./ChatInitializerContext";
import ChatContext from "./ChatContext";
import AppContext from "AppContext";
import NotificationsIndicator from "components/Home/Notifications/Indicator";
import Status from "components/common_components/Status";
import { hasElements } from "utils/arrays";
import { getConversationStatusProps, getConversationPhoneNumber, formatPhoneNumber } from "./utils";
import { useNotifications } from "components/Home/Notifications/Notifications";

function Conversation({ conversation, onClick, isSelected }) {
    const { clientPhotoResolver, conversationNameResolver } = useContext(ChatInitializerContext);
    const { profile } = useContext(ChatContext);
    const notifications = useNotifications(`conversation-${conversation?.sid}`);
    const {
        api: {
            Chat: { Notifications },
        },
        markCachedNotificationsAsRead,
    } = useContext(AppContext);

    const [friendlyName, setFriendlyName] = useState(conversation.friendlyName || conversation.sid);
    const [phoneNumber, setPhoneNumber] = useState("");

    useEffect(() => {
        const getClientInfoAsync = async () => {
            try {
                if (conversationNameResolver) {
                    const fName = await conversationNameResolver(conversation);
                    setFriendlyName(fName);
                }
            } catch (error) {
                console.error("The client info couldn't be loaded.", error);
            }
        };
        getClientInfoAsync();
    }, []);

    useEffect(() => {
        const resolvePhoneNumberAsync = async () => {
            // Prefer the conversation-level SMS binding (sync, works for closed conversations too).
            let address = getConversationPhoneNumber(conversation);
            if (!address) {
                try {
                    const participants = await conversation.getParticipants();
                    const smsParticipant = participants.find((p) => p.type === "sms");
                    address = smsParticipant?.bindings?.sms?.address;
                } catch (error) {
                    console.error("The conversation phone number couldn't be loaded.", error);
                }
            }
            if (address) {
                setPhoneNumber(formatPhoneNumber(String(address)));
            }
        };
        resolvePhoneNumberAsync();
    }, []);

    const notificationsGetter = () => {
        return notifications?.[profile]?.filter((n) => n.conversationSid === conversation.sid);
    };

    const onClickHandler = async () => {
        onClick();
        // Mark notifications as read
        const _notifications = notificationsGetter();
        if (hasElements(_notifications)) {
            const notificationIds = _notifications.map((x) => x.id);
            await Notifications.markNotificationsAsRead(notificationIds);
            // Clear notifications
            markCachedNotificationsAsRead(profile, notificationIds);
        }
    };

    return (
        <div className={clsx("conversation", { active: isSelected })} onClick={onClickHandler}>
            <div className="info-container">
                <div
                    className="photo"
                    style={{
                        backgroundImage: `url(${clientPhotoResolver(conversation)})`,
                    }}
                />
                <div className="info">
                    <p className="name">{friendlyName}</p>
                    {phoneNumber && <p className="phone">{phoneNumber}</p>}
                </div>
            </div>
            <div className="extra-info">
                <Status {...getConversationStatusProps(conversation.state.current)} />
                {conversation.state.current === "active" && (
                    <NotificationsIndicator id={`conversation-${conversation?.sid}`} identity={profile} getter={notificationsGetter} />
                )}
            </div>
        </div>
    );
}

Conversation.propTypes = {
    conversation: PropTypes.shape({ friendlyName: PropTypes.string, sid: PropTypes.string }).isRequired,
    onClick: PropTypes.func.isRequired,
    isSelected: PropTypes.bool.isRequired,
};

export default Conversation;
